/* global PIXI TimelineMax */
import React, { Component } from 'react';
import Frame from '../../views/Frame.jsx';

import Audio from '../../../lib/audio';

import createSprite from '../../../lib/createSprite';
import spritesHelper from '../../../lib/spritesHelper';
import Utils from '../../../lib/utils';

import scenesData from '../../../data/scenes.json';

import FluxSettings from '../../../flux/machine';
import familyFlux from '../../../flux/familyPump';

import PumpersAnimations from '../../../animations/pumpers';
import CommonAnimations from '../../../animations/common';

const propTypes = {
    slideshowKey: React.PropTypes.string,
    backgroundKey: React.PropTypes.string,
    className: React.PropTypes.string,

    onResumeMessageBox: React.PropTypes.func,
    onAnimationReady: React.PropTypes.func,

    fps: React.PropTypes.number,
    fluxSettings: React.PropTypes.object,
    fluxInForeground: React.PropTypes.bool,
    animationData: React.PropTypes.object,

    particlesAcceleration: React.PropTypes.object,
    particlesLifetime: React.PropTypes.object,

    nextFrameMaskGradientStart: React.PropTypes.number,
    nextFrameMaskGradientEnd: React.PropTypes.number,

    hasEmitter: React.PropTypes.bool,
    focus: React.PropTypes.bool,

    width: React.PropTypes.number.isRequired,
    height: React.PropTypes.number.isRequired,
    objs: React.PropTypes.object.isRequired,

    audioUrls: React.PropTypes.object,
};

const defaultProps = {
    slideshowKey: 'jamalFullX',
    backgroundKey: 'kraftBackground',
    className: 'intro-character',
    layers: [
        // {
        //     backgroundKey: 'lightLinesBackground',
        // },
        {
            backgroundKey: 'backgroundStripe',
        },
    ],
    onResumeMessageBox: null,
    onAnimationReady: null,
    fps: 3,
    fluxInForeground: false,
    fluxSettings: familyFlux,
    animationData: null,

    // Particles emitter
    particlesLifetime: {
        min: 1.4,
        max: 1.5,
    },
    particlesAcceleration: {
        x: 0,
        y: 2400,
    },
    nextFrameMaskGradientStart: 0.2,
    nextFrameMaskGradientEnd: 0.4,

    audioUrls: {
        flux: ['/audio/28nov/general-liquide-mouv-01.ogg', '/audio/28nov/general-liquide-mouv-01.mp3'],
    },

    bottomTile: true,
    bottomTileColor: 'gray',
};

const contextTypes = {
    debug: React.PropTypes.bool,
    minIntensity: React.PropTypes.number,
    maxIntensity: React.PropTypes.number,
    cleanTint: React.PropTypes.number,
    intensitiesColors: React.PropTypes.object,
    language: React.PropTypes.string,
};

class Character extends Component {

    constructor(props) {
        super(props);

        this.timeline = null;
        this.sprites = null;
        this.pixiFlux = null;
        this.particlesEmitter = null;

        this.onTick = this.onTick.bind(this);
        this.onStartMessages = this.onStartMessages.bind(this);

        this.build = this.build.bind(this);

        this.play = this.play.bind(this);
        this.pause = this.pause.bind(this);
        this.destroy = this.destroy.bind(this);

        this.animateObjects = this.animateObjects.bind(this);
    }

    componentWillUpdate(nextProps) {
        const animationChanged = this.props.animationData !== nextProps.animationData;
        if (animationChanged) {
            if (this.timeline !== null) {
                this.timeline.seek(nextProps.animationData.time);
                setTimeout(() => {
                    this.timeline.play();
                    setTimeout(() => {
                        this.timeline.pause();
                    }, 300);
                }, 1500);
            }
        }
    }

    onTick() {
        if (this.props.hasEmitter !== true) return;

        if (this.particlesEmitter !== null && this.props.focus) {
            const now = Date.now();
            const delta = (now - this.elapsedTime) * 0.001;
            this.particlesEmitter.update(delta);
            this.elapsedTime = now;
        }
    }

    onStartMessages() {
        if (this.props.onResumeMessageBox) {
            this.props.onResumeMessageBox();
        }
    }

    build(container, timeline, opts) {
        this.sprites = {
            container,
        };
        this.timeline = timeline;
        this.pixiFlux = opts.pixiFlux;

        this.buildObjects();
        if (this.props.hasEmitter === true) {
            this.buildParticlesObjects();
            this.buildParticles();
        }

        this.animateObjects();

        // Audio
        this.audios = {};
        _.forOwn(this.props.audioUrls, _.bind(function(value, key){
            this.audios[key] = new Audio(value, {
                loop: true,
            });
        }, this));
    }

    play() {
        if (this.timeline) {
            if (this.audios.flux) {
                this.audios.flux.fadeInFast();
            }
            this.timeline.seek(0);
            this.timeline.play();
        }
    }

    pause() {
        if (this.timeline) {
            if (this.audios.flux) {
                this.audios.flux.fadeOutFast();
            }
            this.timeline.pause();
        }
    }

    destroy() {
        this.sprites = null;
    }

    buildObjects() {
        const {
            width,
            height,
            objs,
            slideshowKey,
            fluxInForeground,
        } = this.props;

        const sprites = this.sprites;
        const container = sprites.container;

        // flux
        const fluxContainer = new PIXI.Container();
        sprites.flux = fluxContainer;
        if (this.pixiFlux) {
            this.pixiFlux.setContainer(fluxContainer, width);
        }

        if (fluxInForeground !== true) {
            container.addChild(fluxContainer);
        }

        const slideshowObject = objs[slideshowKey];
        sprites[slideshowKey] = createSprite(slideshowObject);

        const slideshow = sprites[slideshowKey];
        slideshow.name = slideshowKey;
        slideshow.userData = { character: true };
        Utils.setSizeAndPositionFromRefSize(slideshow, width, height, slideshowObject);
        container.addChild(slideshow);

        // CONTAINER MASK
        const containerMask = spritesHelper.createFrameMaskFromSize(width, height);
        container.mask = containerMask;
        container.addChild(containerMask);

        if (fluxInForeground === true) {
            container.addChild(fluxContainer);
        }
    }

    buildParticlesObjects() {
        const props = this.props;
        const sprites = this.sprites;
        const container = sprites.container;

        // particles
        const particlesContainer = new PIXI.Container();
        sprites.particles = particlesContainer;
        particlesContainer.name = 'particles';
        this.particlesContainer = particlesContainer;

        // particles mask
        const canvasParticlesMask = document.createElement('canvas');
        canvasParticlesMask.width = (props.width + props.border) * 2;
        canvasParticlesMask.height = props.height;
        const canvasContext = canvasParticlesMask.getContext('2d');
        const nextFrameGradient = canvasContext.createLinearGradient(0, props.height, 0, 0);
        nextFrameGradient.addColorStop(0, 'black');
        nextFrameGradient.addColorStop(props.nextFrameMaskGradientStart, 'black');
        nextFrameGradient.addColorStop(props.nextFrameMaskGradientEnd, 'white');

        canvasContext.fillStyle = 'white';
        canvasContext.fillRect(0, 0, props.width + props.border, props.height);
        canvasContext.fillStyle = nextFrameGradient;
        canvasContext.fillRect(props.width + props.border, 0, props.width, props.height);

        // eslint-disable-next-line new-cap
        const particlesMaskTexture = new PIXI.Texture.fromCanvas(canvasParticlesMask);
        const particlesMask = new PIXI.Sprite(particlesMaskTexture);
        particlesContainer.mask = particlesMask;

        container.addChild(particlesContainer);
        container.addChild(particlesMask);
    }

    buildParticles() {
        this.elapsedTime = Date.now();
        const color = this.context.cleanTint.toString(16);
        const emitter = PumpersAnimations.createParticlesEmitter(this.sprites.particles, this.props.objs.blob.texture, {
            color: {
                start: color,
                end: color,
            },
            acceleration: {
                x: this.props.particlesAcceleration.x,
                y: this.props.particlesAcceleration.y * (this.props.height / 906),
            },
            lifetime: this.props.particlesLifetime,
        });

        this.particlesEmitter = emitter;
    }

    buildAudios() {

    }

    animateObjects() {
        const { slideshowKey } = this.props;
        const sprites = this.sprites;
        const timeline = this.timeline;

        // movieclips
        this.movieclipTimeline = timeline.add(CommonAnimations.getMovieclipTimeline(sprites[slideshowKey], {
            fps: this.props.fps,
            delay: 0,
            repeat: -1,
            onFrameUpdate: (movieclip) => {
                if (this.props.focus && this.props.hasEmitter === true) {
                    const particlesFrames = _.get(this.props.objs, `${movieclip.name}.particles`, null);
                    if (particlesFrames === null) {
                        return;
                    }
                    PumpersAnimations.emitParticles(movieclip, this.particlesEmitter, this.sprites.particles, particlesFrames);
                }
            },
        }), 0);
    }

    updateIntensity() {
        if (this.props.hasEmitter !== true) return;

        const props = this.props;
        const context = this.context;
        const intensity = props.intensity;

        if (this.particlesEmitter !== null) {
            this.particlesEmitter.emit = false;
            const color = context.intensitiesColors[intensity] || context.intensitiesColors[3];

            this.particlesEmitter.startColor = [
                // eslint-disable-next-line no-bitwise
                (color & 0xff0000) >> 16,
                // eslint-disable-next-line no-bitwise
                (color & 0x00ff00) >> 8,
                // eslint-disable-next-line no-bitwise
                (color & 0x0000ff),
            ];
        }
    }

    render() {
        return (
            <Frame
                onBuild={this.build}
                onPlay={this.play}
                onPause={this.pause}
                onDestroy={this.destroy}
                onTick={this.onTick}
                {...this.props}
            />
        );
    }
}

Character.propTypes = propTypes;
Character.defaultProps = defaultProps;
Character.contextTypes = contextTypes;

module.exports = Character;



// WEBPACK FOOTER //
// ./js/components/frames/0-Intro/4-Character.jsx